import { getToken, clearToken } from '../utils/authToken';
import { saveBlobWithPicker } from '../utils/downloadBlob';
import { CreateVariantExplainerModal, SKIP_VARIANT_EXPLAINER_KEY, SKIP_RECOMPOSE_EXPLAINER_KEY } from './imageEditor/CreateVariantExplainerModal';
/**
 * ImageEditor.jsx - Editor de Imágenes Profesional para DamyxIA
 * 
 * Contiene TODAS las herramientas Canvas e IA:
 * - Transformaciones básicas: resize, crop, rotate, flip, ajustes
 * - Transformaciones IA: mejorar, estilo, color, fondo, nitidez, variantes
 * - Edición por selección: región, borrar, sustituir, rellenar
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  RotateCw, RotateCcw, FlipHorizontal, FlipVertical,
  Crop, Move, ZoomIn, ZoomOut, Maximize2, Minimize2,
  Sun, Contrast, Droplet, Focus, Thermometer,
  Wand2, Palette, Image, Layers,
  Sparkles, RefreshCw, Frame,
  Undo2, Redo2, Check, X, RotateCcwIcon,
  Save, Download, Printer, Trash2,
  HelpCircle, Info, ChevronRight, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { BACKEND_URL as API_URL } from '../config/api';
import BackgroundRemovalPanel from './BackgroundRemovalPanel';
import { applyBackgroundRemoval } from '../api/backgroundRemoval';
import { getAOTIProcessingCanvas } from '../services/AOTIService';

// Herramientas disponibles
const TOOLS = {
  SELECT: 'select',
  CROP: 'crop',
  MOVE: 'move'
};

// Categorías de herramientas
const TOOL_CATEGORIES = {
  BASIC: 'basic',
  ADJUSTMENTS: 'adjustments',
  AI: 'ai',
  BACKGROUND: 'background'  // NUEVO: Remoción de fondo
};

const ImageEditor = ({
  workspaceItem,
  imageBase64,
  imageUrl,
  onSave,
  onCreateVariant,
  onClose,
  language = 'es',
  aotiData = null
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const imageRef = useRef(null);
  
  // --- AOTI State ---
  // Dimensiones óptimas para procesamiento IA (inmutables tras carga)
  const [aotiDimensions, setAotiDimensions] = useState(aotiData?.optimized ? aotiData.dimensions : null);
  
  // Estado del editor
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [activeCategory, setActiveCategory] = useState(TOOL_CATEGORIES.BASIC);

  // Vista Completa Historial → al montar, si workspaceItem trae un
  // history_action ("background", "pro", "variants", "generation_normal"),
  // activamos directamente la categoria/tool correspondiente.
  useEffect(() => {
    const action = workspaceItem?.history_action;
    if (!action) return;
    const map = {
      background: { cat: TOOL_CATEGORIES.BACKGROUND, tool: null },
      pro: { cat: TOOL_CATEGORIES.AI, tool: null },
      variants: { cat: TOOL_CATEGORIES.AI, tool: null },
      generation_normal: { cat: TOOL_CATEGORIES.AI, tool: null },
    };
    const m = map[action];
    if (!m) return;
    setActiveCategory(m.cat);
    if (m.tool) setActiveTool(m.tool);
    const hints = {
      background: language === 'es'
        ? 'Describe o elige un fondo nuevo.'
        : 'Describe or choose a new background.',
      pro: language === 'es'
        ? 'Genera imagenes nuevas usando esta como referencia.'
        : 'Generate new images using this as reference.',
      variants: language === 'es'
        ? 'Genera versiones alternativas de esta imagen.'
        : 'Generate alternate versions of this image.',
      generation_normal: language === 'es'
        ? 'Crea una imagen nueva rapidamente.'
        : 'Create a new image quickly.',
    };
    if (hints[action]) {
      import('sonner').then(({ toast }) => toast.info(hints[action])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceItem?.history_action]);
  const [zoom, setZoom] = useState(1);
  const [canvasDims, setCanvasDims] = useState({ w: 0, h: 0 });
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  
  // Ajustes de imagen
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [clarity, setClarity] = useState(0);
  const [temperature, setTemperature] = useState(100); // 100 = neutral
  
  // Estado de crop
  const [cropArea, setCropArea] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState(null);
  // Mobile: menu compacto "Mas IA" (nitidez + variante) junto a Fondo
  const [showAIMoreMenu, setShowAIMoreMenu] = useState(false);
  // 🟥 CUADRO NEGRO 2026-07-18 P7.1 — Modal explicativo previo a "Crear Variante".
  // Directiva de Marcelo (admin/creator): al hacer clic en el botón NO se ejecuta
  // la variante — se abre este modal con la explicación de la feature.
  const [variantExplainerOpen, setVariantExplainerOpen] = useState(false);
  // 🟥 CUADRO NEGRO 2026-07-25 P12 — Modal para "Generar Imagen Nueva a partir del Busto".
  // Comparte el componente CreateVariantExplainerModal pero con mode='recompose'.
  const [recomposeExplainerOpen, setRecomposeExplainerOpen] = useState(false);
  const aiMoreMenuRef = useRef(null);

  // ─── Tabs de categorias: chevrons + fade cuando hay scroll horizontal ───
  const tabsScrollRef = useRef(null);
  const [tabsCanScrollLeft, setTabsCanScrollLeft] = useState(false);
  const [tabsCanScrollRight, setTabsCanScrollRight] = useState(false);

  // Detecta si hay contenido oculto a izquierda/derecha en la barra de tabs
  // para mostrar/ocultar los chevrons y fades indicadores.
  const updateTabsScrollState = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setTabsCanScrollLeft(scrollLeft > 2);
    setTabsCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    updateTabsScrollState();
    el.addEventListener('scroll', updateTabsScrollState, { passive: true });
    // Si cambia el tamano de la ventana (rotacion) recalcular.
    window.addEventListener('resize', updateTabsScrollState);
    return () => {
      el.removeEventListener('scroll', updateTabsScrollState);
      window.removeEventListener('resize', updateTabsScrollState);
    };
  }, [updateTabsScrollState, activeCategory]);

  const scrollTabs = (dir) => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const step = Math.max(120, el.clientWidth * 0.6);
    el.scrollBy({ left: dir === 'right' ? step : -step, behavior: 'smooth' });
  };

  // Cerrar el popover "Mas IA" al hacer click afuera.
  useEffect(() => {
    if (!showAIMoreMenu) return;
    const close = (e) => {
      if (aiMoreMenuRef.current && !aiMoreMenuRef.current.contains(e.target)) {
        setShowAIMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showAIMoreMenu]);
  
  // Estado de resize
  const [resizeWidth, setResizeWidth] = useState(null);
  const [resizeHeight, setResizeHeight] = useState(null);
  const [showResizeModal, setShowResizeModal] = useState(false);

  // Historial para undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Estado de carga
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Imagen original cargada
  const [originalImage, setOriginalImage] = useState(null);
  // Ref persistente a la imagen VERDADERA original (no se sobreescribe al recortar).
  // Se usa solo para "Restaurar original".
  const trueOriginalImageRef = useRef(null);
  const [currentImageData, setCurrentImageData] = useState(null);
  
  // Estado de Background Removal
  const [bgRemovalMode, setBgRemovalMode] = useState('white');
  const [bgRemovalPrompt, setBgRemovalPrompt] = useState('');
  const [isProcessingBackground, setIsProcessingBackground] = useState(false);
  
  // Menú desplegable de estilos
  const [showStyleMenu, setShowStyleMenu] = useState(false);

  // Traducciones
  const t = useCallback((key) => {
    const translations = {
      es: {
        basicTools: 'Herramientas Básicas',
        adjustments: 'Ajustes',
        aiTools: 'Herramientas IA',
        rotate90CW: 'Rotar 90° derecha',
        rotate90CCW: 'Rotar 90° izquierda',
        flipHorizontal: 'Voltear horizontal',
        flipVertical: 'Voltear vertical',
        crop: 'Recortar',
        zoomIn: 'Acercar',
        zoomOut: 'Alejar',
        fitToScreen: 'Ajustar a pantalla',
        brightness: 'Brillo',
        contrast: 'Contraste',
        saturation: 'Saturación',
        clarity: 'Claridad',
        temperature: 'Temperatura',
        resize: 'Redimensionar',
        reduceSize: 'Reducir tamaño',
        enlargeSize: 'Agrandar',
        restore: 'Restaurar original',
        resizeImage: 'Redimensionar imagen',
        width: 'Ancho',
        height: 'Alto',
        keepAspectRatio: 'Mantener proporción',
        apply: 'Aplicar',
        cancel: 'Cancelar',
        enhanceImage: 'Mejorar imagen',
        changeStyle: 'Cambiar estilo',
        changeColor: 'Cambiar color',
        changeBackground: 'Cambiar fondo',
        sharpen: 'Aumentar nitidez',
        createVariant: 'Crear variante',
        applyChanges: 'Aplicar cambios',
        resetChanges: 'Restablecer',
        undo: 'Deshacer',
        redo: 'Rehacer',
        processing: 'Procesando...',
        enhancing: 'Mejorando imagen...',
        changingStyle: 'Cambiando estilo...',
        creatingVariant: 'Creando variante...',
        painting: 'Pintura',
        sketch: 'Boceto',
        design: 'Diseño',
        artistic: 'Artístico',
        selectStylePrompt: 'Selecciona el estilo para transformar la imagen',
        enterColorPrompt: 'Describe el cambio de color deseado',
        enterBackgroundPrompt: 'Describe el nuevo fondo',
        variantCreated: 'Variante creada exitosamente',
        errorCreatingVariant: 'Error al crear variante',
        imageEnhanced: 'Imagen mejorada',
        errorEnhancing: 'Error al mejorar imagen',
        // Toast messages
        errorLoadingImage: 'Error al cargar imagen',
        imageRestored: 'Imagen restaurada',
        resizedTo: 'Redimensionado a',
        noImageToSave: 'No hay imagen para guardar',
        saving: 'Guardando...',
        imageSavedAsVariant: 'Imagen guardada como variante',
        errorSaving: 'Error al guardar',
        noImageToDownload: 'No hay imagen para descargar',
        imageDownloaded: 'Imagen descargada',
        errorDownloading: 'Error al descargar',
        noImageToPrint: 'No hay imagen para imprimir',
        popupBlocked: 'Popup bloqueado',
        noImageToDelete: 'No hay imagen para borrar',
        confirmDeleteImage: '¿Estás seguro de que quieres eliminar esta imagen?',
        imageDeleted: 'Imagen eliminada',
        errorDeleting: 'Error al eliminar',
        noImageToPublish: 'No hay imagen para publicar',
        publishFirstImage: 'Primero publica la imagen',
        imageSentToWorkspace: 'Imagen enviada al área de trabajo',
        errorSendingToWorkspace: 'Error al enviar imagen',
        uploadImage: 'Cargar imagen',
        uploadHint: 'Sube una imagen para comenzar',
        save: 'Guardar',
        download: 'Descargar',
        print: 'Imprimir',
        delete: 'Eliminar',
        close: 'Cerrar',
        imageCropped: 'Imagen recortada',
        selectAreaFirst: 'Selecciona un área primero',
        sendingToIA: 'Enviando a la IA para mejorar...',
        whatToGenerate: '¿Qué quieres generar en la región seleccionada?'
      },
      en: {
        basicTools: 'Basic Tools',
        adjustments: 'Adjustments',
        aiTools: 'AI Tools',
        rotate90CW: 'Rotate 90° right',
        rotate90CCW: 'Rotate 90° left',
        flipHorizontal: 'Flip horizontal',
        flipVertical: 'Flip vertical',
        crop: 'Crop',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        fitToScreen: 'Fit to screen',
        brightness: 'Brightness',
        contrast: 'Contrast',
        saturation: 'Saturation',
        clarity: 'Clarity',
        temperature: 'Temperature',
        resize: 'Resize',
        reduceSize: 'Reduce size',
        enlargeSize: 'Enlarge',
        restore: 'Restore original',
        resizeImage: 'Resize image',
        width: 'Width',
        height: 'Height',
        keepAspectRatio: 'Keep aspect ratio',
        apply: 'Apply',
        cancel: 'Cancel',
        enhanceImage: 'Enhance image',
        changeStyle: 'Change style',
        changeColor: 'Change color',
        changeBackground: 'Change background',
        sharpen: 'Sharpen',
        createVariant: 'Create variant',
        applyChanges: 'Apply changes',
        resetChanges: 'Reset',
        undo: 'Undo',
        redo: 'Redo',
        processing: 'Processing...',
        enhancing: 'Enhancing image...',
        changingStyle: 'Changing style...',
        creatingVariant: 'Creating variant...',
        painting: 'Painting',
        sketch: 'Sketch',
        design: 'Design',
        artistic: 'Artistic',
        selectStylePrompt: 'Select the style to transform the image',
        enterColorPrompt: 'Describe the desired color change',
        enterBackgroundPrompt: 'Describe the new background',
        variantCreated: 'Variant created successfully',
        errorCreatingVariant: 'Error creating variant',
        imageEnhanced: 'Image enhanced',
        errorEnhancing: 'Error enhancing image',
        // Toast messages
        errorLoadingImage: 'Error loading image',
        imageRestored: 'Image restored',
        resizedTo: 'Resized to',
        noImageToSave: 'No image to save',
        saving: 'Saving...',
        imageSavedAsVariant: 'Image saved as variant',
        errorSaving: 'Error saving',
        noImageToDownload: 'No image to download',
        imageDownloaded: 'Image downloaded',
        errorDownloading: 'Error downloading',
        noImageToPrint: 'No image to print',
        popupBlocked: 'Popup blocked',
        noImageToDelete: 'No image to delete',
        confirmDeleteImage: 'Are you sure you want to delete this image?',
        imageDeleted: 'Image deleted',
        errorDeleting: 'Error deleting',
        noImageToPublish: 'No image to publish',
        publishFirstImage: 'Publish the image first',
        imageSentToWorkspace: 'Image sent to workspace',
        errorSendingToWorkspace: 'Error sending image',
        uploadImage: 'Upload image',
        uploadHint: 'Upload an image to start',
        save: 'Save',
        download: 'Download',
        print: 'Print',
        delete: 'Delete',
        close: 'Close',
        imageCropped: 'Image cropped',
        selectAreaFirst: 'Select an area first',
        sendingToIA: 'Sending to AI to enhance...',
        whatToGenerate: 'What do you want to generate in the selected region?'
      }
    };
    return translations[language]?.[key] || translations['en'][key] || key;
  }, [language]);

  // Cargar imagen inicial
  useEffect(() => {
    const loadImage = async () => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        setOriginalImage(img);
        imageRef.current = img;
        // Guardar referencia PERSISTENTE a la verdadera imagen original
        // para que "Restaurar original" funcione incluso despues de recortar
        trueOriginalImageRef.current = img;
        
        // Inicializar canvas
        if (canvasRef.current) {
          const canvas = canvasRef.current;
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          setCurrentImageData(canvas.toDataURL('image/png'));
          
          // AOTI: Fijar dimensiones internas para procesamiento IA
          // Si se recibieron via prop, usar esas. Si no, usar las dimensiones de carga.
          if (aotiData?.optimized && aotiData?.dimensions) {
            setAotiDimensions(aotiData.dimensions);
          } else if (!aotiDimensions) {
            setAotiDimensions({ width: img.width, height: img.height });
          }
          
          // AOTI: Si el usuario rechazó, mostrar advertencia
          if (aotiData && !aotiData.optimized && aotiData.dimensions === null) {
            // Advertencia ya mostrada en WorkspaceImageViewer, no repetir
          }
          
          // Guardar estado inicial
          saveToHistory();
          
          // Auto-ajustar al contenedor con margen de confort (85% del fit = 15% mas pequeno)
          setTimeout(() => {
            const scrollEl = scrollContainerRef.current;
            if (scrollEl && canvasRef.current) {
              const scaleX = (scrollEl.clientWidth - 48) / canvas.width;
              const scaleY = (scrollEl.clientHeight - 48) / canvas.height;
              const newZoom = Math.min(scaleX, scaleY, 1) * 0.85;
              setCanvasDims({ w: canvas.width, h: canvas.height });
              setZoom(Math.max(newZoom, 0.05));
            }
          }, 100);
        }
      };
      
      img.onerror = () => {
        console.error('Error loading image');
        toast.error(t('errorLoadingImage'));
      };
      
      // Cargar desde base64 o URL
      if (imageBase64) {
        const base64Data = imageBase64.startsWith('data:') 
          ? imageBase64 
          : `data:image/png;base64,${imageBase64}`;
        img.src = base64Data;
      } else if (imageUrl) {
        img.src = imageUrl.startsWith('/api') ? `${API_URL}${imageUrl}` : imageUrl;
      }
    };
    
    loadImage();
  }, [imageBase64, imageUrl, language]); // eslint-disable-line react-hooks/exhaustive-deps


  // Redibujar canvas cuando cambian los ajustes
  useEffect(() => {
    if (!originalImage || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Calcular dimensiones con rotación
    const isRotated90 = rotation % 180 !== 0;
    const width = isRotated90 ? originalImage.height : originalImage.width;
    const height = isRotated90 ? originalImage.width : originalImage.height;
    
    canvas.width = width;
    canvas.height = height;
    setCanvasDims({ w: width, h: height });
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    
    // Aplicar filtros CSS
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    
    // Dibujar imagen
    ctx.drawImage(
      originalImage,
      -originalImage.width / 2,
      -originalImage.height / 2
    );
    
    ctx.restore();
    
    // Aplicar claridad (sharpening) si es necesario
    if (clarity > 0) {
      applyClarity(ctx, width, height, clarity);
    }
    
    // Aplicar temperatura
    if (temperature !== 100) {
      applyTemperature(ctx, width, height, temperature);
    }
    
    setCurrentImageData(canvas.toDataURL('image/png'));
  }, [originalImage, rotation, flipH, flipV, brightness, contrast, saturation, clarity, temperature]);

  // Aplicar efecto de temperatura (cálido/frío)
  const applyTemperature = (ctx, width, height, temp) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const factor = (temp - 100) / 100; // -1 a 1
    
    for (let i = 0; i < data.length; i += 4) {
      // Aumentar rojo y reducir azul para cálido (factor > 0)
      // Aumentar azul y reducir rojo para frío (factor < 0)
      if (factor > 0) {
        // Cálido
        data[i] = Math.min(255, data[i] + factor * 30);     // R
        data[i + 2] = Math.max(0, data[i + 2] - factor * 30); // B
      } else {
        // Frío
        data[i] = Math.max(0, data[i] + factor * 30);       // R
        data[i + 2] = Math.min(255, data[i + 2] - factor * 30); // B
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  };

  // Aplicar efecto de claridad/nitidez
  const applyClarity = (ctx, width, height, amount) => {
    if (amount <= 0) return;
    
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const factor = amount / 100;
    
    // Kernel de nitidez simple
    const kernel = [
      0, -factor, 0,
      -factor, 1 + 4 * factor, -factor,
      0, -factor, 0
    ];
    
    const tempData = new Uint8ClampedArray(data);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        for (let c = 0; c < 3; c++) {
          let sum = 0;
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const idx = ((y + ky) * width + (x + kx)) * 4 + c;
              sum += tempData[idx] * kernel[(ky + 1) * 3 + (kx + 1)];
            }
          }
          const idx = (y * width + x) * 4 + c;
          data[idx] = Math.min(255, Math.max(0, sum));
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  };

  // Guardar estado en historial
  const saveToHistory = useCallback(() => {
    if (!canvasRef.current) return;
    
    const newState = {
      imageData: canvasRef.current.toDataURL('image/png'),
      rotation,
      flipH,
      flipV,
      brightness,
      contrast,
      saturation,
      clarity,
      temperature
    };
    
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newState);
      return newHistory.slice(-20); // Mantener máximo 20 estados
    });
    setHistoryIndex(prev => Math.min(prev + 1, 19));
  }, [rotation, flipH, flipV, brightness, contrast, saturation, clarity, temperature, historyIndex]);

  // Deshacer — restaura el snapshot de imageData al canvas y sincroniza
  // originalImage/imageRef para que el redraw useEffect lo respete. Tambien
  // restaura los ajustes (rotation/flip/brightness/…) del estado previo.
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setHistoryIndex(prev => prev - 1);
      // Restaurar ajustes
      setRotation(prevState.rotation);
      setFlipH(prevState.flipH);
      setFlipV(prevState.flipV);
      setBrightness(prevState.brightness);
      setContrast(prevState.contrast);
      setSaturation(prevState.saturation);
      setClarity(prevState.clarity);
      setTemperature(prevState.temperature || 100);
      // Restaurar el contenido de pixeles del canvas (si el estado lo trae)
      if (prevState.imageData && canvasRef.current) {
        const img = new window.Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          setOriginalImage(img);
          imageRef.current = img;
          setCanvasDims({ w: img.width, h: img.height });
          setCurrentImageData(canvas.toDataURL('image/png'));
        };
        img.src = prevState.imageData;
      }
    }
  }, [historyIndex, history]);

  // Rehacer — misma logica que undo pero avanzando en la pila.
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setHistoryIndex(prev => prev + 1);
      setRotation(nextState.rotation);
      setFlipH(nextState.flipH);
      setFlipV(nextState.flipV);
      setBrightness(nextState.brightness);
      setContrast(nextState.contrast);
      setSaturation(nextState.saturation);
      setClarity(nextState.clarity);
      setTemperature(nextState.temperature || 100);
      if (nextState.imageData && canvasRef.current) {
        const img = new window.Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          setOriginalImage(img);
          imageRef.current = img;
          setCanvasDims({ w: img.width, h: img.height });
          setCurrentImageData(canvas.toDataURL('image/png'));
        };
        img.src = nextState.imageData;
      }
    }
  }, [historyIndex, history]);

  // Rotar imagen
  const handleRotate = (degrees) => {
    setRotation(prev => (prev + degrees) % 360);
    saveToHistory();
  };

  // Voltear imagen
  const handleFlip = (horizontal) => {
    if (horizontal) {
      setFlipH(prev => !prev);
    } else {
      setFlipV(prev => !prev);
    }
    saveToHistory();
  };

  // Zoom — pasos relativos (multiplicativos) para que sean predecibles
  // a cualquier escala. Esto evita el bug de saltar de 16% a 25% al pulsar
  // "alejar" cuando la imagen ya estaba muy zoomed-out.
  //
  // - delta > 0  → zoom in (acerca):  zoom *= 1.25
  // - delta < 0  → zoom out (aleja):  zoom *= 0.8
  // - delta == 0 → no-op (compat)
  // Limites: [0.05, 3] (igual que wheel zoom y fitToScreen).
  const handleZoom = (delta) => {
    setZoom(prev => {
      const factor = delta > 0 ? 1.25 : delta < 0 ? 0.8 : 1;
      const next = prev * factor;
      return Math.min(3, Math.max(0.05, next));
    });
  };

  const handleFitToScreen = useCallback(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const scaleX = (scrollEl.clientWidth - 48) / canvas.width;
    const scaleY = (scrollEl.clientHeight - 48) / canvas.height;

    setCanvasDims({ w: canvas.width, h: canvas.height });
    setZoom(Math.max(Math.min(scaleX, scaleY, 1) * 0.85, 0.05));
  }, []);

  // Auto-fit cuando cambia el espacio disponible (tab activo). RAF asegura
  // que el DOM ya aplico el cambio de layout antes de medir.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      handleFitToScreen();
    });
    return () => window.cancelAnimationFrame(id);
  }, [activeCategory, handleFitToScreen]);

  // Restablecer ajustes
  const handleReset = () => {
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setClarity(0);
    setTemperature(100);
    setZoom(1);
    setCropArea(null);
  };

  // Restaurar imagen original
  // Usa la referencia PERSISTENTE a la imagen verdadera original, no
  // `originalImage` (que se sobreescribe con cada Crop/Resize).
  // REGLA: NO modificar tamano, NO usar canvas escalado, respetar el
  // zoom actual del usuario (no forzar setZoom(1) ni auto-fit).
  const handleRestore = () => {
    const trueOriginal = trueOriginalImageRef.current;
    if (!trueOriginal) return;

    // Resetear SOLO los ajustes de imagen (rotation, flip, brightness…).
    // NO tocamos zoom ni cropArea para preservar el layout visible.
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setClarity(0);
    setTemperature(100);

    // Redibujar la imagen verdaderamente original (tamano exacto del original).
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = trueOriginal.width;
      canvas.height = trueOriginal.height;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(trueOriginal, 0, 0);
      setCurrentImageData(canvas.toDataURL('image/png'));
    }

    // Sincronizar estado `originalImage` al verdadero original
    setOriginalImage(trueOriginal);
    imageRef.current = trueOriginal;
    setCanvasDims({ w: trueOriginal.width, h: trueOriginal.height });

    saveToHistory();

    toast.success(t('imageRestored'));
  };

  // Redimensionar imagen
  const handleResize = (newWidth, newHeight) => {
    if (!canvasRef.current || !originalImage) return;
    
    const canvas = canvasRef.current;
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    // Copiar imagen actual
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    tempCtx.drawImage(canvas, 0, 0);
    
    // Redimensionar
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, newWidth, newHeight);
    
    // Actualizar imagen original con las nuevas dimensiones
    const img = new window.Image();
    img.onload = () => {
      setOriginalImage(img);
      imageRef.current = img;
    };
    img.src = canvas.toDataURL('image/png');
    
    setCurrentImageData(canvas.toDataURL('image/png'));
    saveToHistory();
    setShowResizeModal(false);
    
    toast.success(`${t('resizedTo')} ${newWidth}x${newHeight}`);
  };

  // Reducir tamaño (50%)
  const handleReduceSize = () => {
    if (!canvasRef.current) return;
    const newWidth = Math.round(canvasRef.current.width * 0.5);
    const newHeight = Math.round(canvasRef.current.height * 0.5);
    handleResize(newWidth, newHeight);
  };

  // Agrandar tamaño (150%)
  const handleEnlargeSize = () => {
    if (!canvasRef.current) return;
    const newWidth = Math.round(canvasRef.current.width * 1.5);
    const newHeight = Math.round(canvasRef.current.height * 1.5);
    handleResize(newWidth, newHeight);
  };

  // ========== FUNCIONES DE ACCIÓN (BARRA SUPERIOR) ==========
  
  // Salvar imagen editada como nueva variante
  const handleSaveImage = async () => {
    if (!canvasRef.current) {
      toast.error(t('noImageToSave'));
      return;
    }
    
    if (!workspaceItem?.id) {
      toast.error(language === 'es' ? 'No hay item de workspace asociado' : 'No workspace item associated');
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage(t('saving'));
    
    try {
      const token = getToken();
      const imageData = canvasRef.current.toDataURL('image/png');
      
      // Guardar imagen editada como nueva variante del item original
      const response = await axios.post(
        `${API_URL}/api/workspace/items/${workspaceItem.id}/save-as-variant`,
        { base64: imageData },
        { 
          withCredentials: true
        }
      );
      
      
      if (response.data.success) {
        toast.success(t('imageSavedAsVariant'));
        // Notificar al padre para que refresque la lista de variantes
        if (onCreateVariant && response.data.variant) {
          onCreateVariant(response.data.variant);
        }
      }
    } catch (error) {
      console.error('Error saving as variant:', error);
      toast.error(t('errorSaving'));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // Descargar imagen editada
  const handleDownloadImage = async () => {
    if (!canvasRef.current) {
      toast.error(t('noImageToDownload'));
      return;
    }
    
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      // Extraer base64 sin prefijo
      const base64Data = dataUrl.split(',')[1];
      
      // Convertir base64 a bytes binarios
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Crear Blob binario real
      const blob = new Blob([byteArray], { type: 'image/png' });
      const filename = `${workspaceItem?.display_name || 'imagen_editada'}_${Date.now()}.png`;

      const result = await saveBlobWithPicker(blob, filename, 'image/png');
      if (result === 'fallback') {
        toast.success(language === 'es' ? 'Descargada en tu carpeta Descargas' : 'Saved to your Downloads folder');
      } else {
        toast.success(language === 'es'
          ? 'Imagen abierta en nueva pestaña. Click derecho → "Guardar imagen como…" para elegir la carpeta.'
          : 'Image opened in a new tab. Right-click → "Save image as…" to choose the folder.',
          { duration: 7000 }
        );
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error(t('errorDownloading'));
    }
  };

  // Imprimir imagen
  const handlePrintImage = () => {
    if (!canvasRef.current) {
      toast.error(t('noImageToPrint'));
      return;
    }
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error(t('popupBlocked'));
      return;
    }
    
    const imageData = canvasRef.current.toDataURL('image/png');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${workspaceItem?.display_name || 'DamyxIA Image'}</title>
          <style>
            @media print {
              body { margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
            }
            body { margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; }
            img { max-width: 100%; max-height: 90vh; object-fit: contain; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
          </style>
        </head>
        <body>
          <img src="${imageData}" alt="Image" onload="setTimeout(() => { window.print(); window.close(); }, 500);" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Borrar WorkspaceItem actual
  const handleDeleteImage = async () => {
    if (!workspaceItem?.id) {
      toast.error(t('noImageToDelete'));
      return;
    }
    
    const confirmMsg = t('confirmDeleteImage');
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const token = getToken();
      await axios.delete(
        `${API_URL}/api/workspace/items/${workspaceItem.id}`,
        {}
      );
      
      toast.success(t('imageDeleted'));
      // Auto-refresh badge de la Papelera.
      window.dispatchEvent(new CustomEvent('damyxia:trash-updated'));
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(t('errorDeleting'));
    }
  };

  // Aplicar crop
  // cropArea esta en coords CSS (display) del canvas. Convertimos a coords
  // internas del canvas (pixel buffer) para el recorte real.
  const handleApplyCrop = () => {
    if (!cropArea || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const sx = Math.max(0, Math.round(cropArea.x * scaleX));
    const sy = Math.max(0, Math.round(cropArea.y * scaleY));
    const sw = Math.max(1, Math.round(cropArea.width * scaleX));
    const sh = Math.max(1, Math.round(cropArea.height * scaleY));

    const ctx = canvas.getContext('2d');

    // Obtener datos del area de recorte en coords del pixel buffer
    const imageData = ctx.getImageData(sx, sy, sw, sh);

    // Redimensionar canvas al tamano recortado (coords internas)
    canvas.width = sw;
    canvas.height = sh;

    // Dibujar area recortada
    ctx.putImageData(imageData, 0, 0);

    // Actualizar imagen original
    const img = new window.Image();
    img.onload = () => {
      setOriginalImage(img);
      imageRef.current = img;
    };
    img.src = canvas.toDataURL('image/png');

    setCropArea(null);
    setIsCropping(false);
    setActiveTool(TOOLS.SELECT);
    saveToHistory();

    toast.success(t('imageCropped'));
  };

  // Mouse handlers para selección y crop
  // IMPORTANTE: guardamos coordenadas en espacio CSS (display) del canvas,
  // NO en coordenadas internas del canvas. Esto hace que el overlay visual
  // (absolute left/top en px) siga exactamente al cursor a cualquier zoom.
  // La conversion a pixel buffer ocurre solo dentro de handleApplyCrop.
  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Soporte touch: en TouchEvent e.clientX es undefined. Usar touches[0].
    const pt = e.touches && e.touches.length > 0 ? e.touches[0] : e;
    const x = pt.clientX - rect.left;
    const y = pt.clientY - rect.top;

    if (activeTool === TOOLS.CROP) {
      setIsCropping(true);
      setCropStart({ x, y });
      setCropArea({ x, y, width: 0, height: 0 });
    }
  };

  const handleMouseMove = (e) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Soporte touch.
    const pt = e.touches && e.touches.length > 0 ? e.touches[0] : e;
    const x = pt.clientX - rect.left;
    const y = pt.clientY - rect.top;

    if (isCropping && cropStart) {
      setCropArea({
        x: Math.min(cropStart.x, x),
        y: Math.min(cropStart.y, y),
        width: Math.abs(x - cropStart.x),
        height: Math.abs(y - cropStart.y)
      });
    }
  };

  const handleMouseUp = () => {
    setIsCropping(false);
    setCropStart(null);
  };

  // Herramientas IA
  const handleAIEnhance = async () => {
    // Mejorar imagen usando procesamiento de canvas (NO generación IA)
    // Esto preserva la imagen original y solo ajusta calidad
    if (!canvasRef.current) {
      toast.error(language === 'es' ? 'No hay imagen cargada' : 'No image loaded');
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage(t('enhancing'));
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Obtener datos de imagen actuales
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const dataCopy = new Uint8ClampedArray(data);
      
      // Aplicar mejoras de calidad más VISIBLES:
      const contrastFactor = 1.25;    // 25% más contraste
      const saturationFactor = 1.20;  // 20% más saturación
      const brightnessFactor = 1.05;  // 5% más brillo
      
      for (let i = 0; i < data.length; i += 4) {
        let r = data[i];
        let g = data[i + 1];
        let b = data[i + 2];
        
        // Aplicar contraste
        r = ((r / 255 - 0.5) * contrastFactor + 0.5) * 255;
        g = ((g / 255 - 0.5) * contrastFactor + 0.5) * 255;
        b = ((b / 255 - 0.5) * contrastFactor + 0.5) * 255;
        
        // Aplicar brillo
        r *= brightnessFactor;
        g *= brightnessFactor;
        b *= brightnessFactor;
        
        // Aplicar saturación
        const gray = 0.2989 * r + 0.5870 * g + 0.1140 * b;
        r = gray + (r - gray) * saturationFactor;
        g = gray + (g - gray) * saturationFactor;
        b = gray + (b - gray) * saturationFactor;
        
        // Clamp valores
        data[i] = Math.max(0, Math.min(255, r));
        data[i + 1] = Math.max(0, Math.min(255, g));
        data[i + 2] = Math.max(0, Math.min(255, b));
      }
      
      // Aplicar sharpening (nitidez) más fuerte
      const width = canvas.width;
      const height = canvas.height;
      const sharpenAmount = 0.4; // Más intenso
      
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const idx = (y * width + x) * 4;
          
          for (let c = 0; c < 3; c++) {
            const center = dataCopy[idx + c];
            const top = dataCopy[((y - 1) * width + x) * 4 + c];
            const bottom = dataCopy[((y + 1) * width + x) * 4 + c];
            const left = dataCopy[(y * width + (x - 1)) * 4 + c];
            const right = dataCopy[(y * width + (x + 1)) * 4 + c];
            
            const laplacian = 4 * center - top - bottom - left - right;
            const enhanced = data[idx + c] + sharpenAmount * laplacian;
            
            data[idx + c] = Math.max(0, Math.min(255, enhanced));
          }
        }
      }
      
      // Aplicar los cambios al canvas
      ctx.putImageData(imageData, 0, 0);
      
      // Actualizar estado
      setCurrentImageData(canvas.toDataURL('image/png'));
      saveToHistory();
      
      toast.success(t('imageEnhanced'));
      
    } catch (error) {
      console.error('Error enhancing image:', error);
      toast.error(t('errorEnhancing'));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleAIStyleChange = async (style) => {
    // Cambiar estilo usando filtros de procesamiento de imagen
    // Preserva el sujeto original, solo modifica apariencia visual
    if (!canvasRef.current) {
      toast.error(language === 'es' ? 'No hay imagen cargada' : 'No image loaded');
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage(t('changingStyle'));
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;
      
      // Obtener datos de imagen
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      
      switch (style) {
        case 'painting':
          // Efecto pintura al óleo: aumentar saturación, suavizar y aumentar contraste
          for (let i = 0; i < data.length; i += 4) {
            let r = data[i];
            let g = data[i + 1];
            let b = data[i + 2];
            
            // Aumentar saturación (1.4x)
            const gray = 0.2989 * r + 0.5870 * g + 0.1140 * b;
            r = gray + (r - gray) * 1.4;
            g = gray + (g - gray) * 1.4;
            b = gray + (b - gray) * 1.4;
            
            // Aumentar contraste (1.2x)
            r = ((r / 255 - 0.5) * 1.2 + 0.5) * 255;
            g = ((g / 255 - 0.5) * 1.2 + 0.5) * 255;
            b = ((b / 255 - 0.5) * 1.2 + 0.5) * 255;
            
            // Posterizar ligeramente para efecto pintura
            const levels = 12;
            r = Math.round(r / 255 * levels) / levels * 255;
            g = Math.round(g / 255 * levels) / levels * 255;
            b = Math.round(b / 255 * levels) / levels * 255;
            
            data[i] = Math.max(0, Math.min(255, r));
            data[i + 1] = Math.max(0, Math.min(255, g));
            data[i + 2] = Math.max(0, Math.min(255, b));
          }
          break;
          
        case 'sketch':
          // Efecto boceto: convertir a escala de grises con detección de bordes
          const dataCopy = new Uint8ClampedArray(data);
          for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
              const idx = (y * width + x) * 4;
              
              // Calcular gradiente (detección de bordes)
              const getGray = (px, py) => {
                const i = (py * width + px) * 4;
                return 0.2989 * dataCopy[i] + 0.5870 * dataCopy[i + 1] + 0.1140 * dataCopy[i + 2];
              };
              
              const gx = getGray(x + 1, y) - getGray(x - 1, y);
              const gy = getGray(x, y + 1) - getGray(x, y - 1);
              const edge = Math.sqrt(gx * gx + gy * gy);
              
              // Invertir para efecto boceto (líneas oscuras sobre fondo claro)
              const value = 255 - Math.min(255, edge * 2);
              
              data[idx] = value;
              data[idx + 1] = value;
              data[idx + 2] = value;
            }
          }
          break;
          
        case 'design':
          // Efecto diseño moderno: alto contraste, colores vibrantes, bordes definidos
          for (let i = 0; i < data.length; i += 4) {
            let r = data[i];
            let g = data[i + 1];
            let b = data[i + 2];
            
            // Alto contraste (1.5x)
            r = ((r / 255 - 0.5) * 1.5 + 0.5) * 255;
            g = ((g / 255 - 0.5) * 1.5 + 0.5) * 255;
            b = ((b / 255 - 0.5) * 1.5 + 0.5) * 255;
            
            // Aumentar saturación (1.3x)
            const gray = 0.2989 * r + 0.5870 * g + 0.1140 * b;
            r = gray + (r - gray) * 1.3;
            g = gray + (g - gray) * 1.3;
            b = gray + (b - gray) * 1.3;
            
            // Aumentar brillo ligeramente
            r *= 1.05;
            g *= 1.05;
            b *= 1.05;
            
            data[i] = Math.max(0, Math.min(255, r));
            data[i + 1] = Math.max(0, Math.min(255, g));
            data[i + 2] = Math.max(0, Math.min(255, b));
          }
          break;
          
        case 'artistic':
          // Efecto artístico: colores cálidos, viñeta suave, tono vintage
          const centerX = width / 2;
          const centerY = height / 2;
          const maxDist = Math.sqrt(centerX * centerX + centerY * centerY);
          
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              const idx = (y * width + x) * 4;
              let r = data[idx];
              let g = data[idx + 1];
              let b = data[idx + 2];
              
              // Tono cálido/sepia suave
              const gray = 0.2989 * r + 0.5870 * g + 0.1140 * b;
              r = r * 0.7 + gray * 0.3 + 20;
              g = g * 0.8 + gray * 0.2;
              b = b * 0.7 + gray * 0.1;
              
              // Aumentar saturación ligeramente
              const newGray = 0.2989 * r + 0.5870 * g + 0.1140 * b;
              r = newGray + (r - newGray) * 1.15;
              g = newGray + (g - newGray) * 1.15;
              b = newGray + (b - newGray) * 1.15;
              
              // Viñeta suave
              const dist = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
              const vignette = 1 - (dist / maxDist) * 0.3;
              
              data[idx] = Math.max(0, Math.min(255, r * vignette));
              data[idx + 1] = Math.max(0, Math.min(255, g * vignette));
              data[idx + 2] = Math.max(0, Math.min(255, b * vignette));
            }
          }
          break;
      }
      
      // Aplicar cambios
      ctx.putImageData(imageData, 0, 0);
      
      // Actualizar estado
      setCurrentImageData(canvas.toDataURL('image/png'));
      saveToHistory();
      
      const styleNames = {
        painting: language === 'es' ? 'Estilo pintura aplicado' : 'Painting style applied',
        sketch: language === 'es' ? 'Estilo boceto aplicado' : 'Sketch style applied',
        design: language === 'es' ? 'Estilo diseño aplicado' : 'Design style applied',
        artistic: language === 'es' ? 'Estilo artístico aplicado' : 'Artistic style applied'
      };
      
      toast.success(styleNames[style]);
      
    } catch (error) {
      console.error('Error changing style:', error);
      toast.error(t('errorCreatingVariant'));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleAIColorChange = async () => {
    // Cambiar colores usando los controles de ajuste existentes
    // en lugar de generación IA que cambia toda la imagen
    toast.info(
      language === 'es' 
        ? 'Usa los controles de Saturación y Temperatura en "Básico" para ajustar colores' 
        : 'Use Saturation and Temperature controls in "Basic" to adjust colors'
    );
    setActiveCategory(TOOL_CATEGORIES.BASIC);
  };

  const handleAIBackgroundChange = async () => {
    const prompt = window.prompt(t('enterBackgroundPrompt'));
    if (!prompt) return;
    
    if (!workspaceItem?.id) {
      toast.error(t('publishFirstImage'));
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage(t('processing'));
    
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/workspace/items/${workspaceItem.id}/edit`,
        {
          instructions: `Cambia el fondo de la imagen por: ${prompt}`,
          edit_type: 'background'
        },
        {}
      );
      
      if (response.data.success) {
        toast.success(t('variantCreated'));
        if (onCreateVariant) {
          onCreateVariant(response.data.workspace_item);
        }
      }
    } catch (error) {
      console.error('Error changing background:', error);
      toast.error(t('errorCreatingVariant'));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleAISharpen = async () => {
    // Aumentar nitidez usando procesamiento de canvas (NO generación IA)
    if (!canvasRef.current) {
      toast.error(language === 'es' ? 'No hay imagen cargada' : 'No image loaded');
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage(t('processing'));
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;
      
      // Obtener datos de imagen
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      const dataCopy = new Uint8ClampedArray(data);
      
      // Kernel de sharpening (unsharp mask)
      const sharpenAmount = 0.5; // Intensidad del sharpening
      
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const idx = (y * width + x) * 4;
          
          for (let c = 0; c < 3; c++) { // R, G, B
            // Obtener píxeles vecinos
            const center = dataCopy[idx + c];
            const top = dataCopy[((y - 1) * width + x) * 4 + c];
            const bottom = dataCopy[((y + 1) * width + x) * 4 + c];
            const left = dataCopy[(y * width + (x - 1)) * 4 + c];
            const right = dataCopy[(y * width + (x + 1)) * 4 + c];
            
            // Calcular laplaciano (detección de bordes)
            const laplacian = 4 * center - top - bottom - left - right;
            
            // Aplicar sharpening
            const sharpened = center + sharpenAmount * laplacian;
            
            data[idx + c] = Math.max(0, Math.min(255, sharpened));
          }
        }
      }
      
      // Aplicar cambios
      ctx.putImageData(imageData, 0, 0);
      
      // Actualizar estado
      setCurrentImageData(canvas.toDataURL('image/png'));
      saveToHistory();
      
      toast.success(language === 'es' ? 'Nitidez aumentada' : 'Sharpness increased');
      
    } catch (error) {
      console.error('Error sharpening:', error);
      toast.error(t('errorCreatingVariant'));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // 🟥 CUADRO NEGRO 2026-07-18 P7.2 — Skip del explainer + prompt personalizado.
  // 🟥 CUADRO NEGRO 2026-07-25 P12 — Ahora soporta dos modos: 'variant' y 'recompose'.
  const handleOpenVariantExplainer = () => {
    if (!workspaceItem?.id) {
      toast.error(t('publishFirstImage'));
      return;
    }
    let skip = false;
    try { skip = window.localStorage?.getItem(SKIP_VARIANT_EXPLAINER_KEY) === 'true'; }
    catch (_) { /* localStorage bloqueado — ignorar */ }
    if (skip) {
      handleAICreateVariant('', 'variant');
      return;
    }
    setVariantExplainerOpen(true);
  };

  const handleOpenRecomposeExplainer = () => {
    if (!workspaceItem?.id) {
      toast.error(t('publishFirstImage'));
      return;
    }
    let skip = false;
    try { skip = window.localStorage?.getItem(SKIP_RECOMPOSE_EXPLAINER_KEY) === 'true'; }
    catch (_) { /* ignorar */ }
    if (skip) {
      handleAICreateVariant('', 'recompose');
      return;
    }
    setRecomposeExplainerOpen(true);
  };

  const handleAICreateVariant = async (customInstructions = '', mode = 'variant') => {
    if (!workspaceItem?.id) {
      toast.error(t('publishFirstImage'));
      return;
    }

    // 🟥 P7.2 — Si el usuario escribió su propio prompt, lo usamos. Si no,
    // volvemos al prompt genérico según el modo.
    const isRecompose = mode === 'recompose';
    const genericVariant = 'Crea una variante creativa de esta imagen manteniendo la esencia pero con cambios artísticos';
    const genericRecompose = 'creative new cinematic scene';
    const instructions = (customInstructions && customInstructions.trim())
      || (isRecompose ? genericRecompose : genericVariant);

    setIsLoading(true);
    setLoadingMessage(
      isRecompose
        ? (language === 'es' ? 'Generando imagen nueva…' : 'Generating new image…')
        : t('creatingVariant')
    );

    try {
      const response = await axios.post(
        `${API_URL}/api/workspace/items/${workspaceItem.id}/edit`,
        {
          instructions,
          edit_type: isRecompose ? 'recompose' : 'variant'
        },
        {
          // 🟥 CUADRO NEGRO 2026-07-25 P8.2 — Timeout explícito de 180s.
          timeout: 180000,
        }
      );

      if (response.data.success) {
        toast.success(
          isRecompose
            ? (language === 'es' ? 'Imagen nueva generada' : 'New image generated')
            : t('variantCreated')
        );
        if (onCreateVariant) {
          onCreateVariant(response.data.workspace_item);
        }
      }
    } catch (error) {
      // 🟥 CUADRO NEGRO 2026-07-25 P8.2 — Mensajes específicos por status HTTP + network.
      // Antes el default "Error al crear variante" ocultaba causas reales como:
      //   - 502/503/504 (fal.ai / Vision_Replication timeout)
      //   - Network error / axios timeout (proxy corta ≥60s)
      //   - 500 con detail dict en vez de string
      console.error('Error creating variant:', error);
      const status = error?.response?.status;
      const rawDetail = error?.response?.data?.detail;
      const detail = typeof rawDetail === 'string'
        ? rawDetail
        : (rawDetail && typeof rawDetail === 'object' && rawDetail.message)
          ? rawDetail.message
          : '';
      let msg = t('errorCreatingVariant');

      if (status === 401) {
        msg = language === 'es'
          ? 'Sesión expirada — iniciá sesión de nuevo'
          : 'Session expired — please sign in again';
      } else if (status === 402) {
        msg = language === 'es'
          ? 'Alcanzaste tu cuota Pro mensual. Se renueva en el próximo ciclo.'
          : 'You reached your monthly Pro quota. It renews next billing cycle.';
      } else if (status === 403) {
        msg = language === 'es'
          ? 'Necesitás plan Premium para crear variantes'
          : 'You need the Premium plan to create variants';
      } else if (status === 404) {
        msg = language === 'es'
          ? 'La imagen ya no está disponible. Recargá el workspace.'
          : 'The image is no longer available. Please reload the workspace.';
      } else if (status === 400 && detail) {
        msg = detail;
      } else if (status === 500 && detail) {
        const clean = detail.replace(/^Error editing image:\s*/i, '');
        msg = language === 'es'
          ? `El servicio de variantes no pudo procesar la imagen: ${clean}`
          : `Variant service could not process the image: ${clean}`;
      } else if (status === 502 || status === 503 || status === 504) {
        msg = language === 'es'
          ? 'El motor de IA está saturado o tardó demasiado. Reintentá en unos segundos.'
          : 'The AI engine is overloaded or took too long. Please retry in a few seconds.';
      } else if (!error?.response) {
        // Sin response → network/timeout/CORS
        const isTimeout = error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '');
        if (isTimeout) {
          msg = language === 'es'
            ? 'La variante tardó demasiado (>3 min). Reintentá con una imagen más liviana o probá de nuevo.'
            : 'Variant took too long (>3 min). Retry with a lighter image or try again.';
        } else {
          msg = language === 'es'
            ? 'Sin conexión con el servidor. Verificá tu internet y reintentá.'
            : 'No connection to the server. Check your internet and retry.';
        }
      }
      // 🟥 CUADRO NEGRO 2026-07-25 P12.1 — Toast con botón "Reintentar" para
      // errores transitorios. Solo agregamos retry en 502/503/504/timeout/network.
      // NO en 401/402/403/404 (permanentes o requieren acción del usuario).
      const isRetryable =
        status === 502 || status === 503 || status === 504 ||
        (!error?.response) ||
        error?.code === 'ECONNABORTED';
      if (isRetryable) {
        toast.error(msg, {
          action: {
            label: language === 'es' ? '↻ Reintentar' : '↻ Retry',
            onClick: () => handleAICreateVariant(customInstructions, mode),
          },
          duration: 10000,
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // --- AOTI: Helper para obtener canvas a dimensiones óptimas para IA ---
  // Acepta toolType para usar el tamaño óptimo específico de cada herramienta.
  // Tamaños según Módulo Unificado AOTI:
  //   background_removal: 2048, variants: 1536, filters: 1536, upscale: 1024
  const getAOTICanvas = useCallback((toolType) => {
    if (!canvasRef.current) return null;
    return getAOTIProcessingCanvas(canvasRef.current, aotiDimensions, toolType);
  }, [aotiDimensions]);

  // ============================================
  // BACKGROUND REMOVAL - Remoción automática de fondo
  // ============================================
  
  async function handleApplyBackgroundRemoval(mode, backgroundPrompt, edgeOptions = {}) {
    if (!originalImage) {
      toast.error(language === 'es' ? 'No hay imagen cargada' : 'No image loaded');
      return;
    }

    setIsLoading(true);
    setLoadingMessage(
      mode === 'replace'
        ? (language === 'es' ? 'Generando fondo con IA...' : 'Generating AI background...')
        : (language === 'es' ? 'Removiendo fondo...' : 'Removing background...')
    );

    try {
      const token = getToken();
      
      // AOTI: Usar canvas a dimensiones óptimas para procesamiento IA
      // Background Removal = 2048px según Módulo Unificado
      const processingCanvas = getAOTICanvas('background_removal') || canvasRef.current;

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = processingCanvas.width;
      tempCanvas.height = processingCanvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(processingCanvas, 0, 0);
      
      const imageBlob = await new Promise((resolve) => {
        tempCanvas.toBlob(resolve, 'image/png');
      });

      if (!imageBlob || imageBlob.size === 0) {
        console.error('Blob vacío o nulo!');
        throw new Error('No se pudo obtener la imagen');
      }


      const formData = new FormData();
      formData.append("image", imageBlob, "image.png");
      formData.append("mode", mode);

      if (mode === "replace" && backgroundPrompt) {
        formData.append("background_prompt", backgroundPrompt);
      }
      
      // Parámetros avanzados de bordes
      if (edgeOptions.edgeSharpness !== undefined) {
        formData.append("edge_sharpness", edgeOptions.edgeSharpness);
      }
      if (edgeOptions.outlineWidth !== undefined) {
        formData.append("outline_width", edgeOptions.outlineWidth);
      }

      // Limpiar Service Workers viejos que interfieren con POST requests
      if ('serviceWorker' in navigator) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          for (const reg of regs) {
            if (!reg.active || !reg.active.scriptURL.includes('sw-v3')) {
              await reg.unregister();
            }
          }
        } catch (swErr) { /* ignore */ }
      }

      // Verificar que el backend esta listo
      try {
        const healthRes = await fetch(`${API_URL}/api/health`, { method: 'GET' });
        if (!healthRes.ok) {
          throw new Error('Backend no disponible');
        }
      } catch (healthErr) {
        toast.error(language === 'es'
          ? 'El servidor esta inicializando. Espera unos segundos e intenta de nuevo.'
          : 'Server is initializing. Wait a few seconds and try again.');
        setIsLoading(false);
        setLoadingMessage('');
        return;
      }

      // Llamar API con autenticacion + timeout generoso
      const controller = new AbortController();
      const timeoutMs = 120000;
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      const response = await fetch(`${API_URL}/api/background/remove`, {
        method: "POST",
        credentials: 'include',
        body: formData,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Background removal failed");
      }

      const blob = await response.blob();
      const resultUrl = URL.createObjectURL(blob);

      // Cargar resultado en el canvas
      const img = new window.Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Ajustar canvas al tamaño de la imagen resultante
        canvas.width = img.width;
        canvas.height = img.height;
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        // Actualizar estado
        setCurrentImageData(canvas.toDataURL('image/png'));
        
        // Liberar URL temporal
        URL.revokeObjectURL(resultUrl);

        // Sin notificaciones UI: el workspace se actualiza silenciosamente
      };
      
      img.onerror = () => {
        toast.error(
          language === 'es' 
            ? 'Error al cargar la imagen resultante' 
            : 'Error loading result image'
        );
        URL.revokeObjectURL(resultUrl);
      };
      
      img.src = resultUrl;

    } catch (err) {
      console.error('Background removal error:', err);
      let errorMsg;
      if (err.name === 'AbortError') {
        errorMsg = language === 'es'
          ? 'La operación tardó demasiado. Intenta con una imagen más pequeña.'
          : 'Operation timed out. Try with a smaller image.';
      } else if (err.message && err.message.includes('postMessage')) {
        errorMsg = language === 'es'
          ? 'Error temporal del navegador. Recarga la pagina e intenta de nuevo.'
          : 'Temporary browser error. Reload the page and try again.';
      } else {
        errorMsg = err.message || (language === 'es' 
          ? 'Error procesando la imagen' 
          : 'Error processing image');
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }

  // Botón de herramienta - Compacto y profesional
  const ToolButton = ({ icon: Icon, label, onClick, active, disabled }) => (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`flex items-center gap-1 p-2 sm:px-1.5 sm:py-1 rounded-md text-xs transition-all touch-manipulation select-none ${
        active 
          ? 'bg-indigo-600 text-white' 
          : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50 active:bg-zinc-600/70'
      } ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
      title={label}
      aria-label={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden xl:inline">{label}</span>
    </button>
  );

  // Slider de ajuste
  const AdjustmentSlider = ({ label, value, onChange, min = 0, max = 200, defaultValue = 100 }) => (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-400">{label}</span>
        <span className="text-xs text-zinc-500">{value}%</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
      />
      <button
        onClick={() => onChange(defaultValue)}
        className="text-[10px] text-zinc-500 hover:text-zinc-300 self-end"
      >
        Reset
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-zinc-900" data-testid="image-editor-root">
      {/* Barra superior de herramientas - COMPACTA */}
      <div className="flex-shrink-0 border-b border-zinc-700/50 bg-zinc-800/50">
        {/* Wrapper relative para poder posicionar los chevrons y fades
            indicadores encima de la barra scroll. */}
        <div className="relative">
          {/* Chevron izquierdo — visible solo si hay contenido oculto a la izq. */}
          {tabsCanScrollLeft && (
            <button
              onClick={() => scrollTabs('left')}
              className="absolute left-0 top-0 bottom-0 z-20 flex items-center justify-center w-7 bg-gradient-to-r from-zinc-800 via-zinc-800/90 to-transparent text-zinc-300 hover:text-white transition-colors lg:hidden"
              aria-label={language === 'es' ? 'Desplazar izquierda' : 'Scroll left'}
              data-testid="tabs-scroll-left"
            >
              <ChevronLeft className="w-4 h-4 animate-pulse" />
            </button>
          )}

          {/* Chevron derecho — indica que hay mas tabs ocultos a la derecha. */}
          {tabsCanScrollRight && (
            <button
              onClick={() => scrollTabs('right')}
              className="absolute right-0 top-0 bottom-0 z-20 flex items-center justify-center w-7 bg-gradient-to-l from-zinc-800 via-zinc-800/90 to-transparent text-zinc-300 hover:text-white transition-colors lg:hidden"
              aria-label={language === 'es' ? 'Desplazar derecha' : 'Scroll right'}
              data-testid="tabs-scroll-right"
            >
              <ChevronRight className="w-4 h-4 animate-pulse" />
            </button>
          )}

        {/* Tabs de categorías - altura mínima, scroll horizontal en mobile
            para que ninguna categoría se trunque. */}
        <div
          ref={tabsScrollRef}
          className="category-tabs flex items-center gap-0.5 px-2 py-0.5 border-b border-zinc-700/30 overflow-x-auto"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`.category-tabs::-webkit-scrollbar { display: none; }`}</style>
          <button
            onClick={() => setActiveCategory(TOOL_CATEGORIES.BASIC)}
            className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
              activeCategory === TOOL_CATEGORIES.BASIC
                ? 'bg-indigo-600 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50'
            }`}
          >
            {/* Texto corto en mobile para que todo quepa en 1 fila */}
            <span className="lg:hidden">{language === 'es' ? 'Básicas' : 'Basic'}</span>
            <span className="hidden lg:inline">{t('basicTools')}</span>
          </button>
          <button
            onClick={() => setActiveCategory(TOOL_CATEGORIES.ADJUSTMENTS)}
            className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
              activeCategory === TOOL_CATEGORIES.ADJUSTMENTS
                ? 'bg-indigo-600 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50'
            }`}
          >
            {t('adjustments')}
          </button>
          <button
            onClick={() => setActiveCategory(TOOL_CATEGORIES.AI)}
            className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
              activeCategory === TOOL_CATEGORIES.AI
                ? 'bg-indigo-600 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50'
            }`}
          >
            <Sparkles className="w-3 h-3 inline mr-0.5" />
            <span className="lg:hidden">IA</span>
            <span className="hidden lg:inline">{t('aiTools')}</span>
          </button>
          <button
            onClick={() => setActiveCategory(TOOL_CATEGORIES.BACKGROUND)}
            className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
              activeCategory === TOOL_CATEGORIES.BACKGROUND
                ? 'bg-violet-600 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50'
            }`}
            title={language === 'es' ? 'Remover fondo automáticamente' : 'Auto remove background'}
          >
            <Layers className="w-3 h-3 inline mr-0.5" />
            {language === 'es' ? 'Fondo' : 'Background'}
          </button>
          
          {/* Separador */}
          <div className="flex-1" />
          
          {/* Undo/Redo */}
          <button
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="p-1 rounded text-zinc-400 hover:text-white hover:bg-zinc-700/50 disabled:opacity-30"
            title={t('undo')}
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="p-1 rounded text-zinc-400 hover:text-white hover:bg-zinc-700/50 disabled:opacity-30"
            title={t('redo')}
          >
            <Redo2 className="w-4 h-4" />
          </button>
          
          {/* Reset — restaurar imagen original (trueOriginalImageRef) */}
          <button
            onClick={handleRestore}
            className="p-1 rounded text-zinc-400 hover:text-white hover:bg-zinc-700/50"
            title={t('resetChanges')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          
          {/* Separador antes de botones de acción */}
          <div className="w-px h-5 bg-zinc-600 mx-1" />
          
          {/* Botones de acción: Solo iconos, compactos */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={handleSaveImage}
              disabled={isLoading}
              className="p-1.5 text-emerald-400 hover:bg-emerald-900/20 rounded transition-colors disabled:opacity-50"
              title={t('save')}
            >
              <Save className="w-4 h-4" />
            </button>
            <button
              onClick={handleDownloadImage}
              className="p-1.5 text-blue-400 hover:bg-blue-900/20 rounded transition-colors"
              title={t('download')}
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={handlePrintImage}
              className="p-1.5 text-zinc-400 hover:bg-zinc-700/50 rounded transition-colors"
              title={t('print')}
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={handleDeleteImage}
              className="p-1.5 text-red-400 hover:bg-red-900/20 rounded transition-colors"
              title={t('delete')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        </div>
        {/* /wrapper relative — fin chevrons y fade de category-tabs */}
        
        {/* Herramientas de la categoría activa - COMPACTA */}
        <div className="flex items-center gap-1 px-2 py-0.5 overflow-x-auto">
          {activeCategory === TOOL_CATEGORIES.BASIC && (
            <>
              <ToolButton
                icon={RotateCcw}
                label={t('rotate90CCW')}
                onClick={() => handleRotate(-90)}
              />
              <ToolButton
                icon={RotateCw}
                label={t('rotate90CW')}
                onClick={() => handleRotate(90)}
              />
              <div className="w-px h-5 bg-zinc-700" />
              <ToolButton
                icon={FlipHorizontal}
                label={t('flipHorizontal')}
                onClick={() => handleFlip(true)}
                active={flipH}
              />
              <ToolButton
                icon={FlipVertical}
                label={t('flipVertical')}
                onClick={() => handleFlip(false)}
                active={flipV}
              />
              <div className="w-px h-5 bg-zinc-700" />
              <ToolButton
                icon={Crop}
                label={t('crop')}
                onClick={() => setActiveTool(TOOLS.CROP)}
                active={activeTool === TOOLS.CROP}
              />
              {activeTool === TOOLS.CROP && cropArea && (
                <button
                  onClick={handleApplyCrop}
                  className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-500"
                >
                  <Check className="w-4 h-4" />
                  {t('applyChanges')}
                </button>
              )}
              <div className="w-px h-5 bg-zinc-700" />
              <ToolButton
                icon={ZoomOut}
                label={t('zoomOut')}
                onClick={() => handleZoom(-0.25)}
              />
              <span className="text-xs text-zinc-400 min-w-[3rem] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <ToolButton
                icon={ZoomIn}
                label={t('zoomIn')}
                onClick={() => handleZoom(0.25)}
              />
              {/* Ocultos en mobile/tablet (<lg) porque ya existen los
                  botones de zoom que cumplen funcion similar. Visibles solo
                  en desktop. */}
              <div className="hidden lg:flex items-center gap-0.5">
                <ToolButton
                  icon={Maximize2}
                  label={t('fitToScreen')}
                  onClick={handleFitToScreen}
                />
                <div className="w-px h-5 bg-zinc-700" />
                <ToolButton
                  icon={Minimize2}
                  label={t('reduceSize')}
                  onClick={handleReduceSize}
                />
                <ToolButton
                  icon={Maximize2}
                  label={t('enlargeSize')}
                  onClick={handleEnlargeSize}
                />
                <div className="w-px h-5 bg-zinc-700" />
              </div>
              <ToolButton
                icon={RotateCcwIcon}
                label={t('restore')}
                onClick={handleRestore}
              />
            </>
          )}
          
          {activeCategory === TOOL_CATEGORIES.ADJUSTMENTS && (
            <div className="flex items-center gap-3 w-full flex-wrap">
              <div className="flex-1 min-w-[120px] max-w-[140px]">
                <AdjustmentSlider
                  label={t('brightness')}
                  value={brightness}
                  onChange={setBrightness}
                />
              </div>
              <div className="flex-1 min-w-[120px] max-w-[140px]">
                <AdjustmentSlider
                  label={t('contrast')}
                  value={contrast}
                  onChange={setContrast}
                />
              </div>
              <div className="flex-1 min-w-[120px] max-w-[140px]">
                <AdjustmentSlider
                  label={t('saturation')}
                  value={saturation}
                  onChange={setSaturation}
                />
              </div>
              <div className="flex-1 min-w-[120px] max-w-[140px]">
                <AdjustmentSlider
                  label={t('clarity')}
                  value={clarity}
                  onChange={setClarity}
                  min={0}
                  max={100}
                  defaultValue={0}
                />
              </div>
              <div className="flex-1 min-w-[120px] max-w-[140px]">
                <AdjustmentSlider
                  label={t('temperature')}
                  value={temperature}
                  onChange={setTemperature}
                  min={50}
                  max={150}
                  defaultValue={100}
                />
              </div>
            </div>
          )}
          
          {activeCategory === TOOL_CATEGORIES.AI && (
            <>
              <ToolButton
                icon={Wand2}
                label={t('enhanceImage')}
                onClick={handleAIEnhance}
              />
              <div className="w-px h-5 bg-zinc-700" />
              
              {/* Estilos - Botones directos */}
              <div className="flex items-center gap-0.5 bg-zinc-700/30 rounded-lg p-0.5">
                <span className="text-[10px] text-zinc-500 px-1">{t('changeStyle')}:</span>
                <button
                  onClick={() => handleAIStyleChange('painting')}
                  disabled={isLoading}
                  className="px-2 py-1 text-[10px] text-zinc-400 hover:text-white hover:bg-zinc-600/50 rounded transition-all disabled:opacity-50"
                  title={t('painting')}
                >
                  {t('painting')}
                </button>
                <button
                  onClick={() => handleAIStyleChange('sketch')}
                  disabled={isLoading}
                  className="px-2 py-1 text-[10px] text-zinc-400 hover:text-white hover:bg-zinc-600/50 rounded transition-all disabled:opacity-50"
                  title={t('sketch')}
                >
                  {t('sketch')}
                </button>
                <button
                  onClick={() => handleAIStyleChange('design')}
                  disabled={isLoading}
                  className="px-2 py-1 text-[10px] text-zinc-400 hover:text-white hover:bg-zinc-600/50 rounded transition-all disabled:opacity-50"
                  title={t('design')}
                >
                  {t('design')}
                </button>
                <button
                  onClick={() => handleAIStyleChange('artistic')}
                  disabled={isLoading}
                  className="px-2 py-1 text-[10px] text-zinc-400 hover:text-white hover:bg-zinc-600/50 rounded transition-all disabled:opacity-50"
                  title={t('artistic')}
                >
                  {t('artistic')}
                </button>
              </div>
              <ToolButton
                icon={Droplet}
                label={t('changeColor')}
                onClick={handleAIColorChange}
              />
              <ToolButton
                icon={Image}
                label={t('changeBackground')}
                onClick={() => setActiveCategory(TOOL_CATEGORIES.BACKGROUND)}
              />

              {/* Mobile/tablet (<lg): menu compacto "Mas IA" (solo Nitidez, tras eliminar
                  Variantes/Recompose que destruian retratos — P12.4 2026-07-25). */}
              <div className="relative lg:hidden" ref={aiMoreMenuRef}>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAIMoreMenu(v => !v); }}
                  disabled={isLoading}
                  className={`flex items-center justify-center p-1.5 rounded transition-colors ${
                    showAIMoreMenu
                      ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/40'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50 border border-transparent'
                  }`}
                  title={language === 'es' ? 'Mas IA' : 'More AI'}
                  data-testid="ai-more-menu-btn"
                >
                  <Sparkles className="w-4 h-4" />
                </button>
                {showAIMoreMenu && (
                  <div
                    className="absolute top-full mt-1 right-0 z-50 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden min-w-[190px]"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="ai-more-menu"
                  >
                    <button
                      onClick={() => {
                        setShowAIMoreMenu(false);
                        handleAISharpen();
                      }}
                      disabled={isLoading}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
                      data-testid="ai-more-sharpen"
                    >
                      <Focus className="w-4 h-4 text-indigo-400" />
                      <span>{t('sharpen')}</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Desktop-only: Nitidez. Variante y Recompose eliminados (P12.4) */}
              <div className="hidden lg:flex items-center gap-0.5">
                <ToolButton
                  icon={Focus}
                  label={t('sharpen')}
                  onClick={handleAISharpen}
                />
              </div>
            </>
          )}
          
          {/* BACKGROUND REMOVAL - Panel de remoción de fondo */}
          {activeCategory === TOOL_CATEGORIES.BACKGROUND && (
            <BackgroundRemovalPanel
              onApply={handleApplyBackgroundRemoval}
              isLoading={isLoading}
              language={language}
            />
          )}
        </div>
      </div>
      
      {/* Area principal: Canvas */}
      <div className="flex-1 min-h-0 flex">
        {/* Area del canvas - Centrado vertical y horizontal */}
        <div 
          className="flex-1 min-h-0 relative overflow-hidden"
          style={{ 
            backgroundColor: '#1a1a2e',
            backgroundImage: 'linear-gradient(45deg, #1e1e32 25%, transparent 25%), linear-gradient(-45deg, #1e1e32 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1e1e32 75%), linear-gradient(-45deg, transparent 75%, #1e1e32 75%)',
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px'
          }}
        >
        {isLoading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
            <div className="bg-zinc-800 rounded-xl p-4 flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-zinc-300">{loadingMessage}</span>
            </div>
          </div>
        )}

        {/* Contenedor con scroll interno */}
        <div ref={scrollContainerRef} className="absolute inset-0 overflow-auto">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%', minWidth: '100%', padding: '16px', boxSizing: 'border-box' }}>
          <div 
            ref={containerRef}
            className="relative inline-block overflow-visible border border-[#DADADA]"
            style={{ 
              width: canvasDims.w > 0 ? Math.round(canvasDims.w * zoom) + 'px' : undefined,
              height: canvasDims.h > 0 ? Math.round(canvasDims.h * zoom) + 'px' : undefined,
              flexShrink: 0,
              boxShadow: '0 0 6px rgba(0,0,0,0.08)'
            }}
          >
            <canvas
              ref={canvasRef}
              className="block"
              style={{ 
                display: 'block',
                width: '100%',
                height: '100%',
                touchAction: activeTool === TOOLS.CROP ? 'none' : 'auto',
                cursor: activeTool === TOOLS.CROP ? 'crosshair' : 'default'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={(e) => {
                if (activeTool === TOOLS.CROP) {
                  e.preventDefault();
                  handleMouseDown(e);
                }
              }}
              onTouchMove={(e) => {
                if (activeTool === TOOLS.CROP) {
                  e.preventDefault();
                  handleMouseMove(e);
                }
              }}
              onTouchEnd={(e) => {
                if (activeTool === TOOLS.CROP) {
                  e.preventDefault();
                  handleMouseUp(e);
                }
              }}
            />
            
            {/* Overlay de recorte (crop) - posición absoluta relativa al canvas */}
            {cropArea && (
              <div
                className="absolute border-2 border-dashed pointer-events-none z-10"
                style={{
                  left: `${cropArea.x}px`,
                  top: `${cropArea.y}px`,
                  width: `${cropArea.width}px`,
                  height: `${cropArea.height}px`,
                  borderColor: '#22c55e',
                  backgroundColor: 'rgba(34, 197, 94, 0.2)'
                }}
              >
                {/* Handles de resize en las esquinas */}
                <div className="absolute -top-1 -left-1 w-2 h-2 bg-green-500 rounded-sm" />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-sm" />
                <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-green-500 rounded-sm" />
                <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-green-500 rounded-sm" />
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
      </div>

      {/* ═══════ FAB Mobile: Save + Download siempre visibles ═══════
          Resuelve: en celular la toolbar superior a veces se corta al hacer
          scroll lateral y el usuario pierde acceso a "Guardar" y "Descargar".
          Este FAB queda anclado a la esquina inferior derecha y SIEMPRE
          visible mientras se edita cualquier imagen. Oculto en desktop
          (lg:hidden) porque alli la toolbar es suficiente. */}
      <div
        className="lg:hidden fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] z-50 flex flex-col gap-2"
        data-testid="editor-mobile-fab"
      >
        <button
          onClick={handleSaveImage}
          disabled={isLoading}
          className="w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 shadow-lg shadow-emerald-500/40 flex items-center justify-center text-white transition-all disabled:opacity-50 active:scale-95"
          title={t('save')}
          data-testid="editor-fab-save"
        >
          <Save className="w-5 h-5" />
        </button>
        <button
          onClick={handleDownloadImage}
          disabled={isLoading}
          className="w-12 h-12 rounded-full bg-blue-500 hover:bg-blue-400 shadow-lg shadow-blue-500/40 flex items-center justify-center text-white transition-all disabled:opacity-50 active:scale-95"
          title={t('download')}
          data-testid="editor-fab-download"
        >
          <Download className="w-5 h-5" />
        </button>
      </div>

      {/* 🟥 P12.4 2026-07-25 · Modales de Variante y Recompose eliminados del
          render porque los motores image-to-image (PuLID, flux/dev con
          strength 0.15/0.30/0.65) destruian retratos. Marcelo autorizo
          eliminacion definitiva del boton. Handlers y modal-component
          quedan en el codebase por si en el futuro fal.ai saca un modelo
          face-preserving mejor (photomaker-v2, ip-adapter-face-plus-v2, etc). */}

    </div>
  );
};

export default ImageEditor;
